import { mappedLocales } from '@core/Utils/Intl/MappedLocales';

import Button from '@gds/Button/Button';
import { Dropdown } from '@gds/Dropdown/Dropdown';
import { Typography } from '@gds/Typography/Typography';

import { useRangeFilters } from 'Hooks/SearchFilters/UseRangeFilters';

import { FilterControlBaseProps } from '../../FilterPanel.entity';

import { QuickLinks } from '../QuickLinks/QuickLinks';

import { ENGINE_SIZE_RANGE, QUICK_LINKS_RANGE } from './EngineSizeFilter.constants';

import styles from './EngineSizeFilter.module.css';

type EngineSizeFilterProps = FilterControlBaseProps;

export const EngineSizeFilter = ({ locale, strings, tenant }: EngineSizeFilterProps) => {
  const localRange = ENGINE_SIZE_RANGE[locale];
  const minVal = localRange[0].toString();
  const maxVal = localRange[localRange.length - 1].toString();

  const { gteInputProps, lteInputProps, canReset, reset, totalCount } = useRangeFilters(
    'engine-size',
    localRange,
    [minVal, maxVal],
  );

  const lteNum = Number(lteInputProps.value);
  const gteNum = Number(gteInputProps.value);

  const handleChange = (
    opt: Record<string, any> | undefined,
    fn: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => {
    const fakeEvent = {
      target: {
        value: opt?.value,
      },
    };
    fn(fakeEvent as React.FocusEvent<HTMLInputElement, Element>);
  };

  const minOpts = localRange
    .filter(size => size <= lteNum)
    .map(size => ({
      label: size.toFixed(1),
      value: size.toFixed(1),
    }));

  const maxOpts = localRange
    .filter(size => size >= gteNum)
    .map(size => ({
      label: size.toFixed(1),
      value: size.toFixed(1),
    }));

  return (
    <section className={styles.engineSizeFilter}>
      <div className={styles.minMaxInputs}>
        <Dropdown
          dataTestId="engine-size-dd-min"
          options={minOpts}
          value={minOpts.find(opt => opt.value === gteNum?.toFixed(1))}
          onChange={opt => handleChange(opt, gteInputProps.onBlur)}
          uncontrolled
        />
        <Dropdown
          dataTestId="engine-size-dd-max"
          options={maxOpts}
          value={maxOpts.find(opt => opt.value === lteNum?.toFixed(1))}
          onChange={opt => handleChange(opt, lteInputProps.onBlur)}
          uncontrolled
        />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ({totalCount.toLocaleString(mappedLocales[locale])})
        </Typography>
        <Button
          color="secondary"
          name="reset"
          disabled={!canReset()}
          onClick={() => reset()}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <QuickLinks
        filterBaseId="engine-size"
        locale={locale}
        links={QUICK_LINKS_RANGE[locale]}
        strings={strings}
        tenant={tenant}
      />
    </section>
  );
};
